import type { ModuleMigrationDef } from "@kubdee/sdk";
import type { CapabilityName } from "../capabilities";

// Issue #249 M7: build the `capabilities` JSON from the typed CapabilityName
// union so a typo (e.g. "messageing") becomes a compile error instead of a
// runtime connector-type row the registry can never resolve.
const CAPABILITIES = JSON.stringify(["messaging"] satisfies CapabilityName[]);

/**
 * Seed the `line-oa` connector type row (P6 — issue #240).
 *
 * - Idempotent via `INSERT OR IGNORE`: re-running the migration won't clobber
 *   operator edits (icon / label tweaks, capability additions).
 * - Primary key `id` equals `slug` so the capability registry and webhook
 *   ingress route both address the row by the same string. This matches
 *   the Facebook Messenger seed (migration 002).
 * - `oauth_config` carries the Line Login 2.1 endpoints. The PRIMARY auth
 *   mode for Line OA is the long-lived CHANNEL ACCESS TOKEN copy-pasted from
 *   the Line Developers Console — Line Login 2.1 is the *secondary* mode
 *   used only when the OA is linked to a Line Login channel for user sign-in
 *   flows. Flagged via `primaryMode` so the UI can gate the UX correctly.
 * - `webhook_config` records the `x-line-signature` header + HMAC-SHA256
 *   shape the adapter enforces. Helpful for dashboards and debug tooling;
 *   the adapter itself hard-codes the same values in `webhook.ts`.
 * - `icon: 'line'` — the frontend icon registry maps this to the Line brand
 *   glyph. `color: '#00B900'` is the documented Line Green brand color.
 * - `created_at` / `updated_at` use a fixed seed timestamp — tenants-local
 *   time isn't available at seed time, and the column is audit-only.
 */
export const migration: ModuleMigrationDef = {
  key: "003_line_oa_seed",
  version: "0.3.0",
  sql: `
INSERT OR IGNORE INTO \`connectors_types\` (
  id, slug, name, icon, icon_url, capabilities, oauth_config, webhook_config,
  is_active, created_at, updated_at
) VALUES (
  'line-oa',
  'line-oa',
  'Line Official Account',
  'line',
  NULL,
  '${CAPABILITIES}',
  '{"primaryMode":"channel_access_token","note":"Channel access token (copy from Line Developers Console) is the primary credential. Line Login 2.1 endpoints below are used only when the OA is linked to a Line Login channel.","tokenUrl":"https://api.line.me/oauth2/v2.1/token","authorizeUrl":"https://access.line.me/oauth2/v2.1/authorize","clientIdEnv":"LINE_LOGIN_CLIENT_ID","clientSecretEnv":"LINE_LOGIN_CLIENT_SECRET","scopes":["profile","openid"],"channelAccessTokenEnv":"LINE_CHANNEL_ACCESS_TOKEN","channelSecretEnv":"LINE_CHANNEL_SECRET"}',
  '{"signatureHeader":"x-line-signature","algorithm":"HMAC-SHA256","encoding":"base64","secretEnv":"LINE_CHANNEL_SECRET"}',
  1,
  '2026-04-17T00:00:00Z',
  '2026-04-17T00:00:00Z'
);
  `.trim(),
};
